import { useEffect, useMemo, useRef, useState } from 'react';

import { type LoginGrid, type LoginImage, loginImageUrl } from '@/lib/login/loginDesign';

/**
 * 무작위 격자 · 이미지 전환 — 로그인 화면판.
 *
 * <h2>어디서 왔나</h2>
 * 시안 칸의 `ImageShow`(디자인 실험실)를 그대로 옮긴 것이다. 원본은 Codrops 의
 * `Scroll-Transition`(MIT · Hiroki) 둘째 시안 — 이미지를 격자 셀로 썰어 두고 셀을
 * <b>무작위 순서로</b> 켜서 다음 이미지를 드러낸다. 실험실판이 스크롤 대신 시간으로 굴리고
 * GSAP·Lenis 를 안 쓰는 이유는 그쪽 머리말에 있다.
 *
 * <h2>실험실판과 다르게 한 것</h2>
 * <ul>
 *   <li><b>조작판이 없다.</b> 값은 환경설정에서 저장한 것이 내려온다 — 여기서 만질 자리가 아니다.</li>
 *   <li><b>그림은 서버에서 온다.</b> 실험실은 `URL.createObjectURL` 로 그 창에서만 살았고,
 *       여기 있는 것은 모두가 보는 회사 첫 화면이다.</li>
 *   <li><b>글은 사진마다 적어 둔 것</b>이다(`caption`·`subtext`). 실험실은 파일명을 찍었다.</li>
 * </ul>
 *
 * <h2>사진이 한 장이면 넘기지 않는다</h2>
 * 한 장짜리는 «전환» 이 성립하지 않는다 — 그 한 장을 바탕으로 깔고 가만히 둔다. 없으면
 * 아무것도 그리지 않는다(부모가 그때 바탕을 지금 모습으로 되돌린다).
 */

/** 한 셀이 켜지는 데 걸리는 시간(초). 전체 전환 시간에서 이만큼을 뺀 폭에 지연을 흩는다. */
const CELL_SEC = 0.45;

export const LOGIN_GRID_STYLES = `
.lgx{position:absolute;inset:0;overflow:hidden;background:#000}
.lgx-stage{position:absolute;inset:0;overflow:hidden}
/* 밑장 — 지금 보고 있는 그림. 윗장이 다 켜지면 여기로 내려온다. */
.lgx-base{position:absolute;inset:0;background-size:cover;background-position:center;filter:brightness(.8)}
/* 윗장 — 다음 그림을 격자로 썰어 둔 판. 셀 하나씩 켜지면서 밑장을 덮는다. */
.lgx-grid{position:absolute;inset:0;display:grid;
  grid-template-columns:repeat(var(--cols),1fr);grid-template-rows:repeat(var(--rows),1fr)}
.lgx-cell{position:relative;overflow:hidden;opacity:0;
  transition:opacity var(--dur) cubic-bezier(.33,1,.68,1) var(--d,0s)}
.lgx--on .lgx-cell{opacity:1}
.lgx-cell i{position:absolute;display:block;
  width:calc(100% * var(--cols));height:calc(100% * var(--rows));
  left:calc(-100% * var(--x));top:calc(-100% * var(--y));
  background-size:cover;background-position:center;filter:brightness(.8)}

/* 글 — 원본 .txt 의 clip-path 훑기. 그림이 바뀌는 동안 아래에서 올라온다. */
.lgx-txt{position:absolute;left:clamp(20px,4vw,56px);bottom:clamp(48px,7vh,96px);z-index:6;color:#fff;
  pointer-events:none;text-transform:uppercase;text-shadow:0 2px 24px #00000059}
.lgx-txt b{display:block;font-size:clamp(34px,5.4vw,88px);font-weight:400;letter-spacing:-.025em;
  line-height:.85;clip-path:inset(100% 0 0 0);transform:translateY(40px);
  transition:clip-path .9s cubic-bezier(.16,1,.3,1),transform .9s cubic-bezier(.16,1,.3,1)}
.lgx-txt s{display:block;margin-top:14px;font-size:12px;font-weight:600;letter-spacing:.24em;
  text-decoration:none;opacity:0;transition:opacity .6s ease .12s}
.lgx--say .lgx-txt b{clip-path:inset(0 0 0 0);transform:translateY(0)}
.lgx--say .lgx-txt s{opacity:.8}

/* 진행 막대 — 원본 .progress-bar. 칸 하나가 그림 한 장이다. */
.lgx-bar{position:absolute;left:0;right:0;bottom:0;z-index:7;display:flex;gap:10px;padding:20px 24px}
.lgx-bar div{position:relative;flex:1;height:2px;background:#ffffff33;overflow:hidden}
.lgx-bar span{position:absolute;inset:0 auto 0 0;width:0;background:#fff}

@media (prefers-reduced-motion:reduce){
  .lgx-cell{transition:none}
  .lgx-txt b{transition:none;clip-path:inset(0 0 0 0);transform:none}
}
`;

/**
 * `background-image` 주소 한 줄. <b>따옴표를 반드시 두른다</b> — 키에 괄호가 들어가면 따옴표
 * 없는 `url(...)` 은 그 자리에서 토큰이 끊겨 값 전체가 무효가 되고, 화면은 검은 판이 된다.
 */
const asUrl = (key: string | undefined) => (key ? `url("${loginImageUrl(key)}")` : undefined);

/** 0‥n-1 을 섞은 순위표(Fisher–Yates). 같은 화면을 다시 그려도 같은 순서가 나오게 씨앗을 받는다. */
function shuffledRanks(n: number, seed: number): number[] {
  const order = Array.from({ length: n }, (_, i) => i);
  let s = (seed * 2654435761) >>> 0 || 1;
  const rand = () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 100000) / 100000;
  };
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return order;
}

export function LoginGridShow({ images, options }: { images: LoginImage[]; options: LoginGrid }) {
  const stageRef = useRef<HTMLDivElement>(null);

  const [index, setIndex] = useState(0);
  /** 켜는 중인가. 셀 투명도와 글 훑기가 이 값 하나를 본다. */
  const [on, setOn] = useState(false);
  /** 섞는 순서를 갈아 주는 씨앗. 장마다 다른 무늬로 열린다. */
  const [seed, setSeed] = useState(1);
  /** 무대 비율로 낸 줄 수. 칸이 정사각에 가깝게 떨어지도록 가로 칸 수에서 따라온다. */
  const [rows, setRows] = useState(8);

  const { holdSec, switchSec, cols } = options;

  /* 바퀴를 다시 잡는 조건은 <b>사진 목록의 내용</b>이지 배열의 정체가 아니다. 설정 화면에서는
     글자를 한 자 칠 때마다 새 배열이 오는데, 그때마다 첫 장으로 되돌아가면 글을 적는 동안
     미리보기가 한 발짝도 나아가지 못한다. 글자는 그리는 쪽이 매 렌더 새로 읽으므로 그대로 따라온다. */
  const keys = images.map((image) => image.key).join(',');

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    // 비율만 쓰므로 판이 줄어 있어도(설정 화면의 미리보기) 같은 답이 나온다.
    const measure = () => {
      const width = stage.offsetWidth;
      const height = stage.offsetHeight;
      if (!width || !height) return;
      setRows(Math.max(1, Math.round(cols * (height / width))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [cols]);

  const count = images.length;
  const next = count ? (index + 1) % count : 0;
  const cells = cols * rows;

  /** 셀마다의 지연. 섞은 순위를 «전체 전환 시간 − 셀 한 칸» 폭에 고르게 흩는다. */
  const delays = useMemo(() => {
    const spread = Math.max(0, switchSec - CELL_SEC);
    const ranks = shuffledRanks(cells, seed);
    return ranks.map((rank) => (cells > 1 ? (rank / (cells - 1)) * spread : 0));
  }, [cells, seed, switchSec]);

  /* 한 바퀴 — 머문다 → 무작위로 켠다 → 밑장을 갈고 다음 장으로. index 가 바뀌면 이 효과가
     다시 서면서 다음 바퀴가 돈다. 사진 벌이나 값이 바뀌면 그 자리에서 다시 잡는다. */
  useEffect(() => {
    if (count < 2) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let toCommit = 0;
    const toReveal = window.setTimeout(() => {
      setSeed((s) => s + 1);
      setOn(true);
      toCommit = window.setTimeout(
        () => {
          // 밑장을 갈고 켜는 것을 끈다. 격자는 이 순간 <b>새로 서므로</b>(key) 되돌릴 것이 없다.
          setOn(false);
          setIndex((i) => (i + 1) % count);
        },
        reduce ? 0 : switchSec * 1000 + 60,
      );
    }, Math.max(0, holdSec * 1000));

    return () => {
      window.clearTimeout(toReveal);
      window.clearTimeout(toCommit);
    };
  }, [index, count, holdSec, switchSec]);

  /* 사진을 갈아 끼우면(설정 화면의 미리보기) 처음 장부터 다시 돈다 — 새 벌에는 그 자리가 없다. */
  useEffect(() => {
    setIndex(0);
    setOn(false);
  }, [keys]);

  if (!count) return null;

  const shown = images[Math.min(index, count - 1)];
  const coming = images[next];

  return (
    <div
      className={`lgx${on ? ' lgx--on' : ' lgx--say'}`}
      style={
        {
          // 숫자로 넘기지 않는다 — 사용자 속성은 값이 그대로 쓰이므로 문자열이 안전하다.
          '--cols': String(cols),
          '--rows': String(rows),
          '--dur': `${CELL_SEC}s`,
        } as React.CSSProperties
      }
    >
      <div className="lgx-stage" ref={stageRef}>
        <div className="lgx-base" style={{ backgroundImage: asUrl(shown?.key) }} />

        {/* 장이 바뀌면 격자를 <b>통째로 새로</b> 세운다(key). 갓 난 셀은 투명도 0 에서 시작하므로
            「방금 켠 것을 도로 끄는」 장면이 없다. 사진이 한 장뿐이면 윗장 자체를 세우지 않는다. */}
        {count > 1 && (
          <div className="lgx-grid" key={index}>
            {Array.from({ length: cells }, (_, i) => (
              <span
                className="lgx-cell"
                key={i}
                style={{ '--d': `${(delays[i] ?? 0).toFixed(3)}s` } as React.CSSProperties}
              >
                <i
                  style={
                    {
                      '--x': String(i % cols),
                      '--y': String(Math.floor(i / cols)),
                      backgroundImage: asUrl(coming?.key),
                    } as React.CSSProperties
                  }
                />
              </span>
            ))}
          </div>
        )}

        {/* 글을 안 적은 사진에는 아무것도 얹지 않는다 — 파일명을 대신 찍지 않는다. */}
        {shown?.caption && (
          <div className="lgx-txt">
            <b>{shown.caption}</b>
            <s>{shown.subtext ?? `${String(index + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`}</s>
          </div>
        )}

        {count > 1 && (
          <div className="lgx-bar">
            {images.map((image, i) => (
              <div key={image.key}>
                <span
                  style={{
                    width: i <= index ? '100%' : '0%',
                    transition: i === index ? `width ${holdSec}s linear` : 'none',
                    opacity: i <= index ? 1 : 0,
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
